import React, { useMemo, useState } from "react";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import { PACKAGE_SELECTOR_PAGE_SIZE } from "constants/constants";
import { useBasicPackagesInfo } from "store/backend/apiHooks";

import styles from "../PackageSelector/PackageSelector.module.scss";
import viewStyles from "./PackageListView.module.scss";

const getPackageLabel = (pkg) =>
  pkg.displayName || pkg.packageName || `Package ${pkg.packageId}`;

const PackageListView = ({
  selectAll = false,
  selectedIds = [],
  viewMode = false,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { data, isLoading, isError, error: fetchError, refetch } =
    useBasicPackagesInfo();

  const packages = data ?? [];

  const displayPackages = useMemo(() => {
    if (selectAll) return packages;
    return selectedIds
      .map((id) => packages.find((pkg) => pkg.packageId === id))
      .filter(Boolean);
  }, [packages, selectAll, selectedIds]);

  const filteredPackages = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return displayPackages;

    return displayPackages.filter((pkg) => {
      const displayName = (pkg.displayName || "").toLowerCase();
      const packageName = (pkg.packageName || "").toLowerCase();
      const packageId = String(pkg.packageId ?? "");
      return (
        displayName.includes(query) ||
        packageName.includes(query) ||
        packageId.includes(query)
      );
    });
  }, [displayPackages, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredPackages.length / PACKAGE_SELECTOR_PAGE_SIZE));
  const paginatedPackages = filteredPackages.slice(
    (currentPage - 1) * PACKAGE_SELECTOR_PAGE_SIZE,
    currentPage * PACKAGE_SELECTOR_PAGE_SIZE
  );

  if (isLoading) {
    return (
      <div className={viewStyles.statePanel}>
        <p className={styles.stateText}>Loading packages...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className={viewStyles.statePanel}>
        <div className={styles.errorState}>
          <p className={styles.stateText}>
            {fetchError?.message || "Failed to load packages."}
          </p>
          <button type="button" className={styles.retryBtn} onClick={() => refetch()}>
            <RefreshOutlinedIcon fontSize="small" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (selectAll) {
    return (
      <div className={viewStyles.container}>
        <div className={styles.infoBanner}>
          <InfoOutlinedIcon fontSize="small" />
          <span>
            {viewMode
              ? `This coupon applies to all packages${
                  packages.length ? ` (${packages.length} total)` : ""
                }.`
              : `Coupon is applicable to all packages${
                  packages.length ? ` (${packages.length} total)` : ""
                }.`}
          </span>
        </div>

        <div className={viewStyles.panel}>
          <div className={styles.paneHeader}>
            <h3>{viewMode ? "Applicable Packages" : "All Packages"}</h3>
            <span>{filteredPackages.length} total</span>
          </div>

          <div className={viewStyles.toolbar}>
            <div className={styles.searchWrap}>
              <SearchOutlinedIcon className={styles.searchIcon} fontSize="small" />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search packages by name or ID"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          <div className={viewStyles.panelBody}>
            <PackageBoxGrid packages={paginatedPackages} emptyMessage="No packages found." />
          </div>

          <PackagePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={filteredPackages.length}
            onPageChange={setCurrentPage}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={viewStyles.container}>
      <div className={viewStyles.panel}>
        <div className={styles.paneHeader}>
          <h3>
            {viewMode
              ? `Applicable Packages (${displayPackages.length})`
              : `Selected Packages (${displayPackages.length})`}
          </h3>
          <span>{filteredPackages.length} shown</span>
        </div>

        <div className={viewStyles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchOutlinedIcon className={styles.searchIcon} fontSize="small" />
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Search packages by name or ID"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>

        <div className={viewStyles.panelBody}>
          <PackageBoxGrid
            packages={paginatedPackages}
            emptyMessage={
              displayPackages.length
                ? "No packages match your search."
                : viewMode
                  ? "No applicable packages found."
                  : "No packages selected."
            }
          />
        </div>

        <PackagePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={filteredPackages.length}
          onPageChange={setCurrentPage}
        />
      </div>

      {viewMode ? null : (
        <div className={styles.infoBanner}>
          <InfoOutlinedIcon fontSize="small" />
          <span>Coupon is applicable only to the selected packages above.</span>
        </div>
      )}
    </div>
  );
};

const PackageBoxGrid = ({ packages, emptyMessage }) => {
  if (!packages.length) {
    return <p className={viewStyles.emptyState}>{emptyMessage}</p>;
  }

  return (
    <div className={viewStyles.boxGrid}>
      {packages.map((pkg) => (
        <div key={pkg.packageId} className={viewStyles.packageBox}>
          <span className={viewStyles.packageBoxName}>{getPackageLabel(pkg)}</span>
          <span className={viewStyles.packageBoxId}>ID: {pkg.packageId}</span>
        </div>
      ))}
    </div>
  );
};

const PackagePagination = ({
  currentPage,
  totalPages,
  totalCount,
  onPageChange,
}) => (
  <div className={styles.pagination}>
    <span>
      Showing{" "}
      {totalCount === 0 ? 0 : (currentPage - 1) * PACKAGE_SELECTOR_PAGE_SIZE + 1} to{" "}
      {Math.min(currentPage * PACKAGE_SELECTOR_PAGE_SIZE, totalCount)} of {totalCount} packages
    </span>
    <div className={styles.paginationControls}>
      <button
        type="button"
        disabled={currentPage === 1}
        onClick={() => onPageChange((page) => page - 1)}
        aria-label="Previous page"
      >
        <ChevronLeftIcon fontSize="small" />
      </button>
      <span>{currentPage}</span>
      <button
        type="button"
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange((page) => page + 1)}
        aria-label="Next page"
      >
        <ChevronRightIcon fontSize="small" />
      </button>
    </div>
  </div>
);

export default PackageListView;
